import API from "utils/API";
import authHeader from "utils/auth-header";
import { IEmployee } from "../domain/IEmployee";
import { IEmployeeRole } from "../domain/IEmployeeRole";
import { IResponseEmployeeRoles } from "../domain/IReposneEmployeeRoles";
import { IGetAllEmployeesResponse } from "../domain/IGetAllEmployeesResponse";
import { IEmployeeDetails } from "../domain/IEmployeeDetails";
import { IUpdateEmployeeRequestParams } from "../domain/IUpdateEmployeeRequestParams";
import { IMessage } from "domain/IMessage";
import { IPayroll } from "../domain/IPayroll";
import { IGetAllPayrollsForEmployeeResponse } from "../domain/IGetAllPayrollsForEmployeeResponse";
import { ICalculatePayrollForEmployeeRequestParams } from "../domain/ICalculatePayrollForEmployeeRequestParams";
import { ICalculatePayrollForRemainingEmployeesRequestParams } from "../domain/ICalculatePayrollForRemainingEmployeesRequestParams";
import { IPayrollCalculationJob } from "../domain/IPayrollCalculationJob";
import { IPayrollCalculationStatusResponse } from "../domain/IPayrollCalculationStatusResponse";
import { IAddExpenseToPayrollForEmployeeRequestParams } from "../domain/IAddExpenseToPayrollForEmployeeRequestParams";
import { IUpdatePayrollForEmployeeRequestParams } from "../domain/IUpdatePayrollForEmployeeRequestParams";
import { IUpdateExpenseInPayrollForEmployeeRequestParams } from "../domain/IUpdateExpenseInPayrollForEmployeeRequestParams";
import { IDeleteExpenseInPayrollForEmployeeRequestParams } from "../domain/IDeleteExpenseInPayrollForEmployeeRequestParams";
import { ICreateEmployeeRequestParams } from "../domain/ICreateEmployeeRequestParams";
import { IGetAllEmployeesRequestParams } from "../domain/IGetAllEmployeesRequestParams";
import { IPayrollSummary } from "../domain/IPayrollSummary";
import { IGetPayrollSummaryForEmployeeRequestParams } from "../domain/IGetPayrollSummaryForEmployeeRequestParams";
import { getQeuryParams } from "utils/queryParamsUtil";
import { IPayrollsForPrintResponse } from "../domain/IPayrollsForPrintResponse";
import { ICreateEmployeeRoleRequestParams } from "../domain/ICreateEmployeeRoleRequestParams";
import { IEmployeeRefuels } from "../domain/IEmployeeRefuels";
import { IFuelSearch } from "../domain/IFuelSearch";
import { IGetCarnetsRequestParams } from "Dashboard/scenes/Carnets/domain/IGetCarnetsRequestParams";
import { IResponseCarnets } from "Dashboard/scenes/Carnets/domain/IResponseCarnets";
import { IGetPayrollsForPrintRequestParams } from "../domain/IGetPayrollsForPrintRequestParams";

const getEmployeeDetails = async (
  employeeId: number
): Promise<IEmployeeDetails> => {
  var headers = authHeader();
  try {
    const response = await API.get<IEmployeeDetails>(
      `/hr/public/employee/${employeeId}`,
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getAllEmployeeRoles = async (): Promise<IEmployeeRole[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseEmployeeRoles>("/hr/public/role", {
      headers,
    });
    let roles = response.data.roles;
    return roles;
  } catch (err) {
    throw err;
  }
};

const updateEmployee = async (
  data: IUpdateEmployeeRequestParams
): Promise<IEmployeeDetails> => {
  var headers = authHeader();
  const { employeeId, ...rest } = data;
  try {
    const response = await API.put<IEmployeeDetails>(
      `/hr/protected/employee/${employeeId}`,
      { ...rest },
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const deleteEmployee = async (employeeId: number): Promise<IMessage> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IMessage>(
      `/hr/protected/employee/${employeeId}`,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getAllPayrollsForPeriod = async (
  year: number,
  month: number
): Promise<IPayrollSummary> => {
  var headers = authHeader();
  try {
    const response = await API.get<IPayrollSummary>(
      `/hr/protected/payroll/summary/${year}/${month}`,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getAllPayrollsForPrint = async (
  params: IGetPayrollsForPrintRequestParams
): Promise<IPayrollsForPrintResponse> => {
  var headers = authHeader();
  try {
    const response = await API.get<IPayrollsForPrintResponse>(
      `/hr/protected/payroll/print${getQeuryParams(params)}`,
      { headers }
    );
    console.log(response.data);
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getAllPayrollsForEmployee = async (
  employeeId: number
): Promise<IPayroll[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IGetAllPayrollsForEmployeeResponse>(
      `/hr/protected/payroll/${employeeId}`,
      {
        headers,
      }
    );
    return response.data.payrolls;
  } catch (err) {
    throw err;
  }
};

const deletePayrollForEmployee = async (
  employeeId: number,
  payrollId: number
): Promise<IMessage> => {
  var headers = authHeader();
  try {
    const response = await API.delete<IMessage>(
      `/hr/protected/payroll/${employeeId}/${payrollId}`,
      { headers }
    );
    let message = response.data;
    return message;
  } catch (err) {
    throw err;
  }
};

const calculatePayrollForEmployee = async (
  data: ICalculatePayrollForEmployeeRequestParams
): Promise<IPayroll> => {
  var headers = authHeader();
  const { employeeId, ...rest } = data;
  try {
    const response = await API.post<IPayroll>(
      `/hr/protected/payroll/${employeeId}`,
      { ...rest },
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

// Enqueues the payroll calculation for the remaining employees and returns
// immediately with the job to poll (see getPayrollCalculationStatus).
const startPayrollCalculationForRemainingEmployees = async (
  data: ICalculatePayrollForRemainingEmployeesRequestParams
): Promise<IPayrollCalculationJob> => {
  var headers = authHeader();
  try {
    const response = await API.post<IPayrollCalculationJob>(
      "/hr/protected/payroll",
      data,
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getPayrollCalculationStatus = async (
  jobId: string
): Promise<IPayrollCalculationStatusResponse> => {
  var headers = authHeader();
  try {
    const response = await API.get<IPayrollCalculationStatusResponse>(
      `/hr/protected/payroll/calculation/${jobId}`,
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const addExpenseToPayrollForEmployee = async (
  data: IAddExpenseToPayrollForEmployeeRequestParams
): Promise<IPayroll> => {
  var headers = authHeader();
  try {
    const { employeeId, payrollId, ...rest } = data;
    const response = await API.post<IPayroll>(
      `/hr/protected/payroll/${employeeId}/${payrollId}`,
      { ...rest },
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const updatePayrollForEmployee = async (
  data: IUpdatePayrollForEmployeeRequestParams
): Promise<IPayroll> => {
  var headers = authHeader();
  try {
    const { employeeId, payrollId, ...rest } = data;
    const response = await API.put<IPayroll>(
      `/hr/protected/payroll/${employeeId}/${payrollId}`,
      { ...rest },
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const updateExpenseInPayrollForEmployee = async (
  data: IUpdateExpenseInPayrollForEmployeeRequestParams
): Promise<IPayroll> => {
  var headers = authHeader();
  try {
    const { employeeId, payrollId, expenseId, ...rest } = data;
    const response = await API.put<IPayroll>(
      `/hr/protected/payroll/${employeeId}/${payrollId}/${expenseId}`,
      { ...rest },
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const deleteExpenseInPayrollForEmployee = async (
  data: IDeleteExpenseInPayrollForEmployeeRequestParams
): Promise<IPayroll> => {
  var headers = authHeader();
  try {
    const { employeeId, payrollId, expenseId } = data;
    const response = await API.delete<IPayroll>(
      `/hr/protected/payroll/${employeeId}/${payrollId}/${expenseId}`,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getAllEmployees = async (
  data?: IGetAllEmployeesRequestParams
): Promise<IEmployee[]> => {
  var headers = authHeader();
  try {
    const response = await API.get<IGetAllEmployeesResponse>(
      `/hr/public/employee${getQeuryParams(data)}`,
      { headers }
    );
    let employees = response.data.employees;
    return employees;
  } catch (err) {
    throw err;
  }
};

const createEmployee = async (
  data: ICreateEmployeeRequestParams
): Promise<IEmployeeDetails> => {
  var headers = authHeader();
  try {
    const response = await API.post<IEmployeeDetails>(
      "/hr/protected/employee",
      data,
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const createEmployeeRole = async (
  data: ICreateEmployeeRoleRequestParams
): Promise<IEmployeeRole> => {
  var headers = authHeader();
  try {
    const response = await API.post<IEmployeeRole>(
      "/hr/protected/role",
      data,
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getPayrollSummaryForEmployee = async (
  data: IGetPayrollSummaryForEmployeeRequestParams
): Promise<IPayrollSummary> => {
  var headers = authHeader();
  try {
    const { year, month } = data;
    const response = await API.get<IPayrollSummary>(
      `/hr/protected/payroll/summary/${year}/${month}`,
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

const getAllEmployeeRefuels = async (params: IFuelSearch): Promise<IEmployeeRefuels> => {
  var headers = authHeader();
  try {
    const { employeeId, ...rest } = params;
    const response = await API.get<IEmployeeRefuels>(
      `/hr/public/employee/${employeeId}/refuel${getQeuryParams(rest)}`,
      {
        headers,
      }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
}

const getCarnetsInPeriod = async (
  params: IGetCarnetsRequestParams
): Promise<IResponseCarnets> => {
  var headers = authHeader();
  try {
    const response = await API.get<IResponseCarnets>(
      `/hr/protected/carnet${getQeuryParams(params)}`,
      { headers }
    );
    return response.data;
  } catch (err) {
    throw err;
  }
};

export const hrService = {
  getEmployeeDetails,
  getAllEmployeeRoles,
  updateEmployee,
  deleteEmployee,
  getAllPayrollsForEmployee,
  calculatePayrollForEmployee,
  startPayrollCalculationForRemainingEmployees,
  getPayrollCalculationStatus,
  addExpenseToPayrollForEmployee,
  updatePayrollForEmployee,
  deletePayrollForEmployee,
  updateExpenseInPayrollForEmployee,
  deleteExpenseInPayrollForEmployee,
  getAllEmployees,
  createEmployee,
  createEmployeeRole,
  getPayrollSummaryForEmployee,
  getAllPayrollsForPeriod,
  getAllPayrollsForPrint,
  getAllEmployeeRefuels,
  getCarnetsInPeriod,
};
